import { Column, ColumnVisibleEvent } from '@ag-grid-enterprise/all-modules';
import { useMemo } from 'react';
import { useStorageState } from 'react-storage-hooks';

import { CONSTANTS } from '../../../core/config/constants';
import { MainColumns } from '../TableGrid';

export interface Props {
  defaultColumns: MainColumns[];
  columnsStorageId?: string;
}

interface ColumnsStorageCols {
  [key: string]: { hide: boolean; headerName: string };
}

interface ColumnsStorage {
  columns: ColumnsStorageCols;
  version: string;
}

const version = CONSTANTS.COLUMNS_STORAGE.VERSION;
const defaultState = {
  columns: {},
  version,
};

export const useColumnsStorage = ({ defaultColumns, columnsStorageId }: Props) => {
  const defaultColumnsMap = useMemo(() => mapColumnsToObject(defaultColumns), [defaultColumns]);
  const storageKey = CONSTANTS.COLUMNS_STORAGE.KEY_PREFIX + columnsStorageId;
  const [columnsStorage, setColumnsStorage] = useStorageState<ColumnsStorage>(
    // don't change sessionStorage to localStorage
    // it's causes browser freezes due to multiple subscribers to the shared storage
    sessionStorage,
    storageKey,
    defaultState,
  );
  const onColumnChanged = (event: ColumnVisibleEvent) => {
    const columns = getChangedColumns(event.columnApi.getAllColumns() || [], defaultColumnsMap);
    setColumnsStorage({ columns, version });
  };

  if (!columnsStorageId && localStorage.getItem(storageKey)) {
    localStorage.removeItem(storageKey);
  }

  const columnDefs = useMemo(() => {
    if (columnsStorageId && columnsStorage.version === version) {
      return mapColumnsWithStorage(defaultColumns, columnsStorage?.columns);
    } else {
      setColumnsStorage(defaultState);
      return defaultColumns;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultColumns]);

  return { columnDefs, onColumnChanged: columnsStorageId ? onColumnChanged : () => {} };
};

export const mapColumnsToObject = (columns: MainColumns[]): ColumnsStorageCols => {
  return columns.reduce((acc, col) => {
    if (col.field) {
      acc[col.field] = { hide: !!col.hide, headerName: col.headerName || '' };
    }

    return acc;
  }, {} as ColumnsStorageCols);
};

export const mapColumnsWithStorage = (
  columns: MainColumns[],
  savedColumns: ColumnsStorageCols,
): MainColumns[] => {
  return columns.map((col) => {
    if (col.field && savedColumns[col.field]) {
      col.hide = savedColumns[col.field]?.hide;
    }

    return col;
  });
};

export const getChangedColumns = (
  columns: Column[],
  defaultColumnsMap: ColumnsStorageCols,
): ColumnsStorageCols => {
  return columns.reduce((acc, column: Column) => {
    const field = column.getColDef()?.field;
    const headerName = column.getColDef()?.headerName;
    if (
      field &&
      defaultColumnsMap[field] &&
      !column.isVisible() !== !!defaultColumnsMap[field]?.hide
    ) {
      acc[field] = { hide: !column.isVisible(), headerName: headerName || '' };
    }

    return acc;
  }, {} as ColumnsStorageCols);
};
